import { z } from 'zod';

import { simplePaginationSchema } from './pagination';

export const changeLogEventDetailsTypeSchema = z.union([
    z.literal('created'),
    z.literal('deleted'),
    z.literal('edited'),
    z.literal('moved'),
    z.literal('assigned_reviewer'),
    z.literal('unassigned_reviewer'),
    z.literal('reviewed'),
    z.literal('published'),
    z.literal('copied'),
    z.literal('version_created'),
]);

export type ChangeLogEventDetailsType = z.infer<typeof changeLogEventDetailsTypeSchema>;

export const changeLogEventEntityTypeSchema = z.union([
    z.literal('root'),
    z.literal('family'),
    z.literal('cluster'),
    z.literal('role'),
    z.literal('level'),
]);

export type ChangeLogEventEntityType = z.infer<typeof changeLogEventEntityTypeSchema>;

export const changeLogEventActorSchema = z.object({
    id: z.uuid(),
    emailAddress: z.string().optional(),
    firstName: z.string(),
    lastName: z.string(),
});

export type ChangeLogEventActor = z.infer<typeof changeLogEventActorSchema>;

const changeLogEntitySchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    type: changeLogEventEntityTypeSchema.optional(),
});

export type ChangeLogEntity = z.infer<typeof changeLogEntitySchema>;

const changeLogEventDetailsBaseSchema = z.object({
    type: changeLogEventDetailsTypeSchema,
});

const changeLogEventCreateDetailSchema = changeLogEventDetailsBaseSchema.extend({
    entity: changeLogEntitySchema,
    parent: changeLogEntitySchema.optional(),
    type: z.literal('created'),
});

const changeLogEventDeleteDetailSchema = changeLogEventDetailsBaseSchema.extend({
    entity: changeLogEntitySchema,
    parent: changeLogEntitySchema.optional(),
    type: z.literal('deleted'),
});

const changeLogEventEditDetailSchema = changeLogEventDetailsBaseSchema.extend({
    entity: changeLogEntitySchema,
    field: z.string(),
    newValue: z.string(),
    oldValue: z.string(),
    type: z.literal('edited'),
});

const changeLogEventMoveDetailSchema = changeLogEventDetailsBaseSchema.extend({
    entity: changeLogEntitySchema,
    from: changeLogEntitySchema,
    to: changeLogEntitySchema,
    type: z.literal('moved'),
});

const changeLogEventAssignReviewerDetailSchema = changeLogEventDetailsBaseSchema.extend({
    entity: changeLogEntitySchema,
    parent: changeLogEntitySchema.optional(),
    reviewer: changeLogEventActorSchema,
    type: z.literal('assigned_reviewer'),
});

const changeLogEventUnassignReviewerDetailSchema = changeLogEventDetailsBaseSchema.extend({
    entity: changeLogEntitySchema,
    parent: changeLogEntitySchema.optional(),
    reviewer: changeLogEventActorSchema,
    type: z.literal('unassigned_reviewer'),
});

const changeLogEventReviewDetailSchema = changeLogEventDetailsBaseSchema.extend({
    entity: changeLogEntitySchema,
    parent: changeLogEntitySchema.optional(),
    reviewer: changeLogEventActorSchema,
    type: z.literal('reviewed'),
    version: z.number().int().positive(),
});

const changeLogEventPublishDetailSchema = changeLogEventDetailsBaseSchema.extend({
    entity: changeLogEntitySchema,
    parent: changeLogEntitySchema.optional(),
    publisher: changeLogEventActorSchema,
    type: z.literal('published'),
    version: z.number().int().positive(),
});

const changeLogEventCopiedDetailSchema = changeLogEventDetailsBaseSchema.extend({
    entity: changeLogEntitySchema,
    from: changeLogEntitySchema,
    to: changeLogEntitySchema,
    type: z.literal('copied'),
});

const changeLogEventVersionCreatedDetailSchema = changeLogEventDetailsBaseSchema.extend({
    entity: changeLogEntitySchema,
    type: z.literal('version_created'),
    version: z.number().int().positive(),
});

export const changeLogEventDetailsSchema = z.discriminatedUnion('type', [
    changeLogEventCreateDetailSchema,
    changeLogEventDeleteDetailSchema,
    changeLogEventEditDetailSchema,
    changeLogEventMoveDetailSchema,
    changeLogEventAssignReviewerDetailSchema,
    changeLogEventUnassignReviewerDetailSchema,
    changeLogEventReviewDetailSchema,
    changeLogEventPublishDetailSchema,
    changeLogEventCopiedDetailSchema,
    changeLogEventVersionCreatedDetailSchema,
]);

export type ChangeLogEventDetails = z.infer<typeof changeLogEventDetailsSchema>;

export type ChangeLogEventCreateDetail = z.infer<typeof changeLogEventCreateDetailSchema>;
export type ChangeLogEventDeleteDetail = z.infer<typeof changeLogEventDeleteDetailSchema>;
export type ChangeLogEventEditDetail = z.infer<typeof changeLogEventEditDetailSchema>;
export type ChangeLogEventMoveDetail = z.infer<typeof changeLogEventMoveDetailSchema>;
export type ChangeLogEventAssignReviewerDetail = z.infer<typeof changeLogEventAssignReviewerDetailSchema>;
export type ChangeLogEventUnassignReviewerDetail = z.infer<typeof changeLogEventUnassignReviewerDetailSchema>;
export type ChangeLogEventReviewDetail = z.infer<typeof changeLogEventReviewDetailSchema>;
export type ChangeLogEventPublishDetail = z.infer<typeof changeLogEventPublishDetailSchema>;
export type ChangeLogEventCopiedDetail = z.infer<typeof changeLogEventCopiedDetailSchema>;
export type ChangeLogEventVersionCreatedDetail = z.infer<typeof changeLogEventVersionCreatedDetailSchema>;

export const changeLogEventSchema = z.object({
    actor: changeLogEventActorSchema.optional().nullable(),
    changeId: z.uuid(),
    details: changeLogEventDetailsSchema,
    entityType: changeLogEventEntityTypeSchema,
    eventId: z.uuid(),
    itemId: z.uuid().nullable(),
    timestamp: z.iso.datetime(),
});

export type ChangeLogEvent = z.infer<typeof changeLogEventSchema>;

export const jobArchitectureHistorySchema = changeLogEventSchema.array();

export type JobArchitectureHistory = z.infer<typeof jobArchitectureHistorySchema>;

export const paginatedJobArchitectureHistorySchema = z.object({
    data: changeLogEventSchema.array(),
    pagination: simplePaginationSchema,
});

export type PaginatedJobArchitectureHistory = z.infer<typeof paginatedJobArchitectureHistorySchema>;
